/**
 * Приведение заголовков из бэкенда к виду интерфейса.
 *
 * В базе заголовки хранятся с порядковой нумерацией и разнобоем в оформлении:
 * «1.Укажите вашу специальность.», «Модуль 1. Зачем врачу личный бренд».
 * Нумерация дублирует то, что и так видно по положению в списке, а точка в
 * конце заголовка выглядит как обрыв фразы.
 *
 * Это оформление, а не изменение данных: сами ответы и запросы работают с
 * исходными значениями.
 *
 * Значение может отсутствовать: в базе поля заголовков допускают null, и
 * падать из-за этого целым экраном недопустимо.
 */

type Title = string | null | undefined;

/** Убирает ведущий номер и точку в конце: «1.Текст.» → «Текст». */
export function cleanQuestionTitle(source: Title): string {
  const title = source ?? '';
  const cleaned = title
    .trim()
    .replace(/^\d+\s*[.)]\s*/, '')
    .replace(/\.\s*$/, '')
    .trim();
  return cleaned || title.trim();
}

/**
 * Убирает приставку «Модуль N.»: «Модуль 1. Название» → «Название».
 * Если после чистки ничего не остаётся, возвращается исходный заголовок:
 * пустая строка вместо названия хуже лишней нумерации.
 */
export function cleanModuleTitle(source: Title): string {
  const title = source ?? '';
  const cleaned = title
    .trim()
    .replace(/^модуль\s*\d+\s*[.)]?\s*/i, '')
    .trim();
  return cleaned || title.trim();
}

/** Убирает ведущий номер урока: «3. Название» → «Название». */
export function cleanLessonTitle(source: Title): string {
  const title = source ?? '';
  const cleaned = title.trim().replace(/^\d+\s*[.)]\s*/, '').trim();
  return cleaned || title.trim();
}

/**
 * Заголовок и пояснение вопроса.
 *
 * Часть вопросов приходит в две строки: сама формулировка и пояснение под ней
 * («…в своей сфере?\nПоделитесь ссылками…»). Одним заголовком это читается как
 * сплошная фраза, поэтому вторая строка становится подписью. Отдельное поле
 * `description` в схеме важнее: если оно есть, разбирать текст не нужно.
 */
export function questionParts(question: { question?: Title; description?: Title }): {
  title: string;
  description: string;
} {
  // Пустые строки в начале отбрасываются: иначе заголовок оказывается пустым,
  // а сама формулировка с номером уезжает в пояснение под ним
  const [first, ...rest] = (question.question ?? '').trimStart().split('\n');
  const inlineDescription = rest.join('\n').trim();
  const description = (question.description ?? '').trim() || inlineDescription;
  const title = cleanQuestionTitle(first);

  // Без формулировки заголовком становится пояснение: строка анкеты профиля
  // с одним ответом и без названия не говорит, на что этот ответ
  if (!title && description) {
    const [descriptionFirst, ...descriptionRest] = description.split('\n');
    return { title: cleanQuestionTitle(descriptionFirst), description: descriptionRest.join('\n').trim() };
  }

  return { title, description };
}
